import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Badge,
  Popover,
  ListItemButton,
  ListItemText,
  alpha,
  TextField,
  InputAdornment,
  Divider,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search,
  NotificationsNone,
  Logout,
  AccountCircleRounded,
  MenuOpenRounded,
  SensorsRounded,
  ChevronRightRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { relativeTime } from '../../lib/format';
import { palette } from '../../theme';
import { StatusPill } from '../ui/GlassPanel';

const SEVERITY_COLOR = {
  critical: palette.danger,
  warning: palette.warning,
  info: palette.primary,
};

export default function TopBar({ user, onToggleSidebar, onLogout, title, subtitle, onOpenNotifications }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const knownIds = useRef(new Set());
  const firstLoad = useRef(true);

  const loadAlerts = async () => {
    try {
      const response = await api.get('/alerts/all', { params: { status: 'active', limit: 25 } });
      const list = response.data || [];
      setAlerts(list);

      if (firstLoad.current) {
        list.forEach((alert) => knownIds.current.add(alert.id));
        firstLoad.current = false;
      }
      setUnread(list.filter((alert) => !alert.is_acknowledged).length);
    } catch (error) {
      /* notifications are non critical */
    }
  };

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 15000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return alerts
      .filter(
        (alert) =>
          alert.device_name?.toLowerCase().includes(term) ||
          alert.message?.toLowerCase().includes(term) ||
          alert.alert_type?.toLowerCase().includes(term)
      )
      .slice(0, 6);
  }, [search, alerts]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (!search.trim()) return;
    navigate(`/devices?search=${encodeURIComponent(search.trim())}`);
  };

  const initials = (user?.full_name || user?.username || '?')
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, md: 2 },
        px: { xs: 2, md: 3 },
        py: 1.75,
        backdropFilter: 'blur(24px) saturate(140%)',
        WebkitBackdropFilter: 'blur(24px) saturate(140%)',
        background: `linear-gradient(180deg, ${alpha('#050816', 0.92)}, ${alpha('#050816', 0.62)})`,
        borderBottom: `1px solid ${alpha(palette.primary, 0.1)}`,
      }}
    >
      <IconButton
        onClick={onToggleSidebar}
        sx={{
          display: { lg: 'none' },
          color: 'text.secondary',
          background: alpha(palette.primary, 0.08),
          border: `1px solid ${alpha(palette.primary, 0.16)}`,
        }}
      >
        <MenuOpenRounded />
      </IconButton>

      <Box sx={{ minWidth: 0, flex: { xs: 1, md: '0 1 auto' } }}>
        <Typography
          sx={{
            fontSize: { xs: '1.02rem', md: '1.28rem' },
            fontWeight: 700,
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.68rem', md: '0.74rem' }, color: 'text.secondary', mt: 0.15 }}>
          {subtitle}
        </Typography>
      </Box>

      <Box sx={{ flex: 1 }} />

      {/* console-wide search */}
      <Box
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{ display: { xs: 'none', md: 'block' }, width: 300, position: 'relative' }}
      >
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search devices, alerts, operators…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 17, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.82rem' } }}
        />
        {results.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              right: 0,
              zIndex: 20,
              borderRadius: 3,
              border: `1px solid ${alpha(palette.primary, 0.2)}`,
              background: alpha('#0B1224', 0.97),
              backdropFilter: 'blur(18px)',
              boxShadow: '0 26px 54px -24px rgba(0,0,0,0.95)',
              overflow: 'hidden',
            }}
          >
            {results.map((alert) => (
              <ListItemButton
                key={alert.id}
                onClick={() => {
                  setSearch('');
                  navigate(`/alerts`);
                }}
                sx={{ py: 1, borderBottom: `1px solid ${alpha(palette.primary, 0.07)}` }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    mr: 1.4,
                    bgcolor: SEVERITY_COLOR[alert.severity] || palette.primary,
                    boxShadow: `0 0 8px ${SEVERITY_COLOR[alert.severity] || palette.primary}`,
                  }}
                />
                <ListItemText
                  primary={alert.device_name}
                  secondary={alert.message}
                  primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 700 }}
                  secondaryTypographyProps={{ fontSize: '0.7rem', noWrap: true }}
                />
              </ListItemButton>
            ))}
          </Box>
        )}
      </Box>

      {/* notifications */}
      <Tooltip title="Active alerts">
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            color: 'text.secondary',
            background: alpha(palette.primary, 0.07),
            border: `1px solid ${alpha(palette.primary, 0.16)}`,
          }}
        >
          <Badge
            badgeContent={unread}
            color="error"
            sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', fontWeight: 700, animation: unread ? 'holoPulse 2s infinite' : 'none' } }}
          >
            <NotificationsNone sx={{ fontSize: 19 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* profile */}
      <Tooltip title={user?.full_name || user?.username}>
        <IconButton
          onClick={(event) => setProfileAnchor(event.currentTarget)}
          sx={{ p: 0.4, border: `1px solid ${alpha(palette.primary, 0.2)}`, background: alpha(palette.primary, 0.08) }}
        >
          <Avatar
            sx={{
              width: 30,
              height: 30,
              fontSize: '0.72rem',
              fontWeight: 700,
              background: `linear-gradient(140deg, ${palette.primary}, ${palette.secondary})`,
              color: '#04070F',
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 360,
              maxWidth: '92vw',
              borderRadius: 3.5,
              border: `1px solid ${alpha(palette.primary, 0.18)}`,
              background: `linear-gradient(165deg, ${alpha('#0C1428', 0.98)}, ${alpha('#050816', 0.98)})`,
              backdropFilter: 'blur(22px)',
              boxShadow: '0 30px 70px -30px rgba(0,0,0,0.95)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.25, py: 1.75 }}>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>Active alerts</Typography>
          <StatusPill label={`${unread} unacknowledged`} tone={unread ? 'danger' : 'success'} />
        </Box>
        <Divider sx={{ borderColor: alpha(palette.primary, 0.1) }} />
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {alerts.length === 0 ? (
            <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>No active alerts in the fleet.</Typography>
            </Box>
          ) : (
            alerts.map((alert) => (
              <ListItemButton
                key={alert.id}
                onClick={() => {
                  setAnchorEl(null);
                  navigate('/alerts');
                }}
                sx={{ py: 1.35, px: 2.25, borderBottom: `1px solid ${alpha(palette.primary, 0.06)}`, alignItems: 'flex-start' }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.35 }}>
                    <SensorsRounded sx={{ fontSize: 14, color: SEVERITY_COLOR[alert.severity] || palette.primary }} />
                    <Typography className="mono" sx={{ fontSize: '0.74rem', fontWeight: 600 }}>
                      {alert.device_name}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>
                      {relativeTime(alert.created_at)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.45 }}>
                    {alert.message}
                  </Typography>
                  <Box sx={{ mt: 0.75 }}>
                    <StatusPill label={alert.severity} tone={alert.severity === 'critical' ? 'danger' : alert.severity} />
                  </Box>
                </Box>
                <ChevronRightRounded sx={{ fontSize: 16, color: alpha(palette.secondaryText, 0.6), mt: 0.5 }} />
              </ListItemButton>
            ))
          )}
        </Box>
        <Box sx={{ p: 1.25, borderTop: `1px solid ${alpha(palette.primary, 0.1)}` }}>
          <Box
            component="button"
            onClick={() => {
              setAnchorEl(null);
              navigate('/alerts');
            }}
            sx={{
              width: '100%',
              py: 1,
              borderRadius: 2.5,
              border: `1px solid ${alpha(palette.primary, 0.2)}`,
              background: alpha(palette.primary, 0.07),
              color: palette.primary,
              fontFamily: 'inherit',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Open alert center
          </Box>
        </Box>
      </Popover>

      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2.25, py: 1.5, minWidth: 210 }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>
            {user?.full_name || user?.username}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{user?.email}</Typography>
          <Box sx={{ mt: 1 }}>
            <StatusPill label={user?.role === 'admin' ? 'Administrator' : 'Operator'} tone={user?.role === 'admin' ? 'secondary' : 'info'} />
          </Box>
        </Box>
        <Divider sx={{ borderColor: alpha(palette.primary, 0.1) }} />
        <MenuItem
          onClick={() => {
            setProfileAnchor(null);
            navigate('/profile');
          }}
          sx={{ fontSize: '0.82rem', gap: 1.25 }}
        >
          <AccountCircleRounded sx={{ fontSize: 17, color: 'text.secondary' }} />
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            setProfileAnchor(null);
            onLogout();
          }}
          sx={{ fontSize: '0.82rem', gap: 1.25, color: palette.danger }}
        >
          <Logout sx={{ fontSize: 17 }} />
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
}
